import { LIB } from './library';
import type { Project } from './types';

/*
 * Drawing standard of a project. IEC 60617 is the library's own graphics;
 * NFPA 79 / JIC (North America) swaps the symbols that look different there
 * (contacts, coils, push buttons, pilot lights, breakers, overloads) and uses
 * the NFPA device designations (CR, M, PB, LT, OL, CB…). Pins, connection
 * points and bounding boxes stay the same, so a project can switch at any time.
 */

export type DrawingStandard = 'iec' | 'nfpa';

export const standardOf = (pr: Pick<Project, 'settings'> | undefined): DrawingStandard =>
  pr?.settings?.standard === 'nfpa' ? 'nfpa' : 'iec';

const P = (d: string, c?: string) => `<path d="${d}"${c ? ` class="${c}"` : ''}/>`;
const C = (x: number, y: number, r: number) => `<circle cx="${x}" cy="${y}" r="${r}"/>`;
const T = (x: number, y: number, s: number, t: string) =>
  `<text x="${x}" y="${y}" font-size="${s}" text-anchor="middle">${t}</text>`;
const p3 = (f: (x: number) => string) => [0, 8, 16].map(f).join('');

/** Contact as two plates across the wire (open: gap between them). */
const plates = (x: number, y: number, h: number) => `M${x} 0V${y}M${x} ${y + 3}V${h}M${x - 3} ${y}H${x + 2}M${x - 3} ${y + 3}H${x + 2}`;
const NO = P(plates(0, 6.5, 16));
const NC = P(plates(0, 6.5, 16) + 'M-3.4 11.6L2 4.4');
/** Push button: two terminals with a bridging bar moved by the plunger. */
const PB_T = C(0, 5, 0.9) + C(0, 11, 0.9) + P('M0 0V4.1M0 11.9V16');
const PB_NO = PB_T + P('M-3 3.5V12.5M-3 8H-9');
// normally closed: the bar touches the terminals from the far side, the stem passing between them (IEEE 315 4.7)
const PB_NC = PB_T + P('M0.9 3.5V12.5M-9 8H0.9');
const PUSH = P('M-9 5.5V10.5');
const MUSHROOM = P('M-9 4.5V11.5M-9 4.5A3.5 3.5 0 0 0 -9 11.5');
/** Limit switch: hinged blade with the actuating wedge. */
const LS_T = C(0, 4, 0.9) + C(0, 12, 0.9) + P('M0 0V3.1M0 12.9V16');
const WEDGE = P('M-2.2 7.6L-5 8.6L-3.9 5.9Z');
/** Circuit breaker pole: open blade under the arc. */
const cbPole = (x: number) => C(x, 8, 0.8) + C(x, 16, 0.8) + P(`M${x} 0V7.2M${x} 16.8V24M${x - 0.5} 15.3L${x - 2.2} 8.9M${x - 2} 6.4A7 7 0 0 0 ${x - 2} 17.6`);
/** Thermal overload heater of one pole. */
const heater = (x: number, h: number) => P(`M${x} 0V${h / 2 - 3}a3 3 0 0 1 0 6V${h}`);
const rays = [[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([a, b]) => `M${a * 3.3} ${8 + b * 3.3}L${a * 4.6} ${8 + b * 4.6}`).join('');
/*
 * Process switches in the IEEE 315 pictorial form (turned to the vertical wire): the blade between two terminal
 * circles (open: past the far terminal; closed: resting on it), a stem from the blade carrying the actuator.
 */
const SW_NO = LS_T + P('M-0.6 11.3L-4.4 4.6M-2.5 8H-6');
const SW_NC = LS_T + P('M-0.6 11.3L1.6 3.4M0.5 8H-6');
const DIAPHRAGM = P('M-8.5 6V10M-8.5 6A2 2 0 0 1 -8.5 10');
const FLOAT_BALL = C(-7.5, 8, 1.5);
const FLAG = P('M-6 8H-9L-9 11Z');
const CRANK = P('M-6 8V6.5H-7.5V9.5H-9V8H-10');
/** Proximity switch: the contact inside a diamond (static, contactless device). */
const DIAMOND = P('M0 0V2.5M0 13.5V16M0 2.5L5.5 8L0 13.5L-5.5 8Z');
const LAMP = P('M0 0V4.5M0 11.5V16' + rays) + C(0, 8, 3.5);

const SVG: Record<string, string> = {
  no: NO,
  nc: NC,
  // timed contacts, ladder form: NO + TC and NC + TO on-delay, NO + TO and NC + TC off-delay (IEEE 315 4.16)
  no_t: NO + T(-6, 9, 2.4, 'TC'),
  nc_t: NC + T(-6, 9, 2.4, 'TO'),
  no_toff: NO + T(-6, 9, 2.4, 'TO'),
  nc_toff: NC + T(-6, 9, 2.4, 'TC'),
  k1: NO,
  k3: p3((x) => P(plates(x, 8.5, 20))) + P('M-2 10H18', 'dash'),
  k3y: p3((x) => P(plates(x, 8.5, 20))) + P('M-2 10H18', 'dash') + P('M0 20H16M8 20V22'),
  coil: P('M0 0V5.5M0 14.5V20') + C(0, 10, 4.5),
  // mechanical interlock: IEEE 315 4.29, "indicated by notes", on the dashed mechanical connection (14.1.1)
  mint: P('M0 0H24', 'dash') + T(12, -1, 1.8, 'MECH. INTERLOCK'),
  coil_on: P('M0 0V5.5M0 14.5V20') + C(0, 10, 4.5),
  coil_off: P('M0 0V5.5M0 14.5V20') + C(0, 10, 4.5),
  pb_no: PB_NO + PUSH,
  pb_nc: PB_NC + PUSH,
  estop: PB_NC + MUSHROOM,
  estop_no: PB_NO + MUSHROOM,
  ls_no: LS_T + P('M-0.6 11.3L-4.4 4.6') + WEDGE,
  // the wedge under the blade of the closed contact
  ls_nc: LS_T + P('M-0.6 11.3L1.6 3.4') + P('M0.3 8L-2.5 9L-1.4 6.3Z'),
  lamp: LAMP,
  lamp_led: LAMP,
  lamp_flash: LAMP + P('M5 6.5H6V3.5H8V6.5H9'),
  pb_latch: PB_NO + PUSH + P('M-6.8 9.4L-5.8 8L-4.8 9.4'),
  // selector switch: the contact pair and the operating lever with its arrow (positions in a truth table)
  rot01: PB_NO + P('M-9 8L-10.5 5M-10.5 5L-10.6 6.6M-10.5 5L-9.3 6'),
  rot_no: PB_NO + P('M-9 8L-10.5 5M-10.5 5L-10.6 6.6M-10.5 5L-9.3 6'),
  tstat: SW_NO + CRANK,
  ps_no: SW_NO + DIAPHRAGM,
  ps_nc: SW_NC + DIAPHRAGM,
  fs_no: SW_NO + FLOAT_BALL,
  fs_nc: SW_NC + FLOAT_BALL,
  flow_no: SW_NO + FLAG,
  flow_nc: SW_NC + FLAG,
  tstat_nc: SW_NC + CRANK,
  prox_no: DIAMOND + P('M-3 6.5H3M-3 9.5H3'),
  prox_nc: DIAMOND + P('M-3 6.5H3M-3 9.5H3M-3.4 11.6L3.4 4.4'),
  ind_no: DIAMOND + P('M-3 6.5H3M-3 9.5H3'),
  ind_nc: DIAMOND + P('M-3 6.5H3M-3 9.5H3M-3.4 11.6L3.4 4.4'),
  // horn: a square and its flare; buzzer: a square with a slanted line from its corner (IEEE 315 10.1)
  horn: P('M0 0V5M0 11V16') + `<rect x="-2.5" y="5" width="5" height="6"/>` + P('M2.5 6L7.5 3V13L2.5 10'),
  buzzer: P('M0 0V5M0 11V16') + `<rect x="-2.5" y="5" width="5" height="6"/>` + P('M2.5 5L5 2.5'),
  cb1: cbPole(0),
  cb3: p3(cbPole) + P('M-5 12H13', 'dash'),
  ol1: heater(0, 16),
  ol3: p3((x) => heater(x, 20)),
  motor3: P('M0 0L2.3 10.3M8 0V8M16 0L13.7 10.3M24 0V16H16') + C(8, 16, 8) + T(8, 17.4, 3.6, 'MTR'),
};

/** NFPA 79 device designations by symbol type. */
const PREFIX: Record<string, string> = {
  sol: 'SOL', coil: 'CR', no: 'CR', nc: 'CR', no_t: 'TR', nc_t: 'TR', no_toff: 'TR', nc_toff: 'TR', coil_on: 'TR', coil_off: 'TR', co: 'CR', k1: 'CR', k3: 'CR', k3y: 'CR',
  cb3: 'CB', cb1: 'CB', cb3n: 'CB', cb3m: 'CB', fuse: 'FU', ol3: 'OL', ol1: 'OL',
  motor3: 'MTR', motor6: 'MTR', vfd: 'VFD', ss: 'SST', gen3: 'GEN', vt: 'T', ct: 'CT', psu: 'PWS', rect: 'BC', battery: 'BAT', heat: 'HTR',
  pb_no: 'PB', pb_nc: 'PB', pb_latch: 'PB', estop: 'PB', estop_no: 'PB', rot01: 'SS', rot_no: 'SS', rot012: 'SS',
  ls_no: 'LS', ls_nc: 'LS', tstat: 'TAS', tstat_nc: 'TAS', lamp: 'LT', lamp_led: 'LT', lamp_flash: 'LT', lamp_tube: 'LT', horn: 'AH', buzzer: 'ABU', res: 'RES',
  ps_no: 'PS', ps_nc: 'PS', fs_no: 'FS', fs_nc: 'FS', flow_no: 'FLS', flow_nc: 'FLS', prox_no: 'PRS', prox_nc: 'PRS', ind_no: 'PRS', ind_nc: 'PRS', ds3: 'DISC', fsd3: 'DISC', cap3: 'CAP',
  amp: 'AM', volt: 'VM', vmr: 'VSR', watt: 'WM', hz: 'FM',
  sl_cb: 'CB', sl_k: 'CR', sl_ol: 'OL', sl_fuse: 'FU', sl_vfd: 'VFD', sl_ss: 'SST', sl_m: 'MTR', sl_g: 'GEN', sl_tr: 'T', sl_ds: 'DISC', sl_lbs: 'DISC', sl_st: 'M', sl_yd: 'M', sl_ct: 'CT', sl_spd: 'SA', sl_meter: 'WHM', sl_kwh: 'WHM', sl_fsd: 'DISC', plug: 'PL', pin_m: 'PL', pin_f: 'RECP', cbct: 'CT', efr: 'GFR', kwh: 'WHM', kwh1: 'WHM', kwh3: 'WHM',
};

/** IEC letter → NFPA designation for generated circuits (Q breaker, K contactor…). */
// G (power supply) is PWS: PS is the pressure switch in NFPA 79 Annex E
export const NFPA_LETTER: Record<string, string> = { Q: 'CB', K: 'CR', M: 'MTR', S: 'PB', H: 'LT', T: 'VFD', G: 'PWS', F: 'OL' };

/** Library SVG of a symbol type in the given standard. */
export const svgFor = (type: string, std: DrawingStandard = 'iec') => (std === 'nfpa' && SVG[type]) || LIB[type]?.svg || '';

/** Whether the standard draws this type differently from IEC. */
export const hasStandardSvg = (type: string, std: DrawingStandard) => std === 'nfpa' && !!SVG[type];

/** Device tag prefix of a symbol type in the given standard. */
export const prefixFor = (type: string, std: DrawingStandard = 'iec') => (std === 'nfpa' && PREFIX[type]) || LIB[type]?.prefix || '';
